type OfficialDocument = {
  id: string;
  type: string;
  name: string;
  status: string;
  url?: string;
  rejectionReason?: string;
};

/** Display labels only: preserve the original filename for downloads. */
export function officialDocumentTitle(document: Pick<OfficialDocument, 'type' | 'name'>) {
  if (document.type === 'ds-2019') return 'DS-2019';
  const title = document.name
    .replace(/\.pdf$/i, '')
    .replace(/^signed[-_]agreement[-_]/i, '')
    .replace(/[-_][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\bparticipant terms\b/i, 'Participant Agreement')
    .trim();
  if (!title || title === 'file') return document.type === 'signed-agreement' ? 'Signed Agreement' : 'Program Document';
  return title.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function officialBadge(document: OfficialDocument) {
  if (document.status === 'verified') return { label: 'Verified', cls: 'ok' };
  if (['approved', 'accepted', 'signed'].includes(document.status)) {
    return {
      label: ['signed-agreement', 'terms-acceptance'].includes(document.type) ? 'Signed' : 'Approved',
      cls: 'ok',
    };
  }
  if (document.status === 'rejected') return { label: 'Rejected', cls: 'err' };
  if (document.status === 'in_review') return { label: 'In review', cls: 'warn' };
  if (['pending', 'pending_review', 'uploaded'].includes(document.status)) return { label: 'Pending review', cls: 'warn' };
  return { label: 'Status unavailable', cls: 'muted' };
}

export default function OfficialDocumentRow({
  document,
  signedOn,
  opening,
  onView,
  onDownload,
}: {
  document: OfficialDocument;
  signedOn?: string;
  opening: boolean;
  onView: () => void;
  onDownload: () => void;
}) {
  const title = officialDocumentTitle(document);
  const badge = officialBadge(document);
  return (
    <div className="docrow" data-testid={`official-document-${document.id}`}>
      <div className="docmain">
        <strong title={document.name}>{title}</strong>{' '}
        <span className={`pill ${badge.cls}`} data-testid={`document-status-${document.id}`}>{badge.label}</span>
        <div className="sub muted">
          {document.type === 'signed-agreement' ? 'Signed Agreement' : document.type === 'terms-acceptance' ? 'Terms & Conditions' : 'Official document'}
          {signedOn ? ` · Signed ${signedOn}` : ''}
        </div>
        {document.status === 'rejected' && document.rejectionReason && (
          <div className="sub globus-msg" role="alert">{document.rejectionReason}</div>
        )}
      </div>
      {document.url && (
        <div className="docactions">
          <a href="#" aria-label={`View ${title}`} data-testid={`view-document-${document.id}`}
            onClick={(event) => { event.preventDefault(); onView(); }}>
            {opening ? 'Opening…' : 'View'}
          </a>
          <a href="#" aria-label={`Download ${title}`} data-testid={`download-document-${document.id}`}
            onClick={(event) => { event.preventDefault(); onDownload(); }}>
            Download
          </a>
        </div>
      )}
    </div>
  );
}